// Enhanced authentication service with bot detection and role-based access
import { 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword,
  signOut,
  sendPasswordResetEmail,
  updateProfile
} from 'firebase/auth';
import { 
  doc, 
  setDoc, 
  getDoc, 
  updateDoc, 
  serverTimestamp,
  collection,
  query,
  where,
  getDocs
} from 'firebase/firestore';
import { auth, db } from '../firebase';
import {
  performBotDetection,
  trackLoginAttempt,
  blockUserIfBot,
  shouldChallengeUser,
  verifyCaptcha
} from './botDetectionService';
import { DISCOUNT_CODES } from './discountService';

// ============================================================
// SHARED SECURITY HELPER — used by enhancedRegister, loginWithGoogle, updateUserProfile
// Checks A (staff email domain) and B (name impersonation). Throws on failure.
// ============================================================
// Block impersonation of the FOUNDERS by full name only. We intentionally do NOT
// block the bare surnames "burnham"/"agouras" — those are legitimate last names a
// real prospective member could have. Only full-name matches of the actual
// founders are rejected (incident APN-INC-2026-04-09-001).
const BLOCKED_NAME_SUBSTRINGS = [
  'caroline burnham',
  'caroline agouras',
  'nicholas agouras',
  'nick agouras',
  'helix agouras',
];

export function validateSignupIdentity(email, firstName, lastName, displayName, source = 'signup') {
  const normalizedEmail = (email || '').trim().toLowerCase();
  const fullName = `${firstName || ''} ${lastName || ''} ${displayName || ''}`.toLowerCase();

  // Check A — staff domain block
  if (normalizedEmail.endsWith('@the-apn.com')) {
    console.warn('[SECURITY] signup rejected:', { check: `A-${source}`, email: normalizedEmail, name: fullName, timestamp: new Date().toISOString() });
    throw new Error('This email domain is reserved for APN staff. Please sign up with your business email.');
  }

  // Check B — name impersonation block
  if (BLOCKED_NAME_SUBSTRINGS.some(s => fullName.includes(s))) {
    console.warn('[SECURITY] signup rejected:', { check: `B-${source}`, email: normalizedEmail, name: fullName, timestamp: new Date().toISOString() });
    throw new Error('This name cannot be used for registration. Please contact APN staff directly if you believe this is an error.');
  }
}

// User roles and permissions
export const USER_ROLES = {
  GUEST: 'guest',
  PENDING: 'pending',
  MEMBER: 'member',
  VIP_MEMBER: 'vip_member',
  ADMIN: 'admin',
  SUPER_ADMIN: 'super_admin',
  CHAPTER_PRESIDENT: 'chapter_president',
  AMBASSADOR: 'ambassador'
};

export const ROLE_PERMISSIONS = {
  [USER_ROLES.GUEST]: [
    'view_public_content',
    'register_account'
  ],
  [USER_ROLES.PENDING]: [
    'view_public_content',
    'view_pending_status'
  ],
  [USER_ROLES.MEMBER]: [
    'view_public_content',
    'view_member_content',
    'access_member_directory',
    'join_groups',
    'post_in_groups',
    'register_for_events',
    'apply_to_jobs',
    'apply_to_projects'
  ],
  [USER_ROLES.VIP_MEMBER]: [
    'view_public_content',
    'view_member_content',
    'view_vip_content',
    'access_member_directory',
    'join_groups',
    'post_in_groups',
    'create_groups',
    'register_for_events',
    'apply_to_jobs',
    'apply_to_projects',
    'post_jobs',
    'post_projects'
  ],
  [USER_ROLES.CHAPTER_PRESIDENT]: [
    'view_public_content',
    'view_member_content',
    'view_vip_content',
    'access_member_directory',
    'join_groups',
    'post_in_groups',
    'create_groups',
    'moderate_groups',
    'register_for_events',
    'create_events',
    'manage_chapter_events',
    'apply_to_jobs',
    'apply_to_projects',
    'post_jobs',
    'post_projects'
  ],
  [USER_ROLES.ADMIN]: [
    'view_public_content',
    'view_member_content',
    'view_vip_content',
    'view_admin_content',
    'access_member_directory',
    'manage_users',
    'approve_members',
    'join_groups',
    'post_in_groups',
    'create_groups',
    'moderate_groups',
    'delete_groups',
    'register_for_events',
    'create_events',
    'manage_all_events',
    'apply_to_jobs',
    'apply_to_projects',
    'post_jobs',
    'post_projects',
    'moderate_content',
    'access_analytics'
  ],
  [USER_ROLES.SUPER_ADMIN]: [
    'all_permissions'
  ]
};

// Enhanced login with bot detection
export const enhancedLogin = async (email, password, captchaResponse = null) => {
  try {
    // Perform bot detection (non-blocking — log only, don't block until CAPTCHA UI is built)
    let botDetection = { isBot: false, botScore: 0 };
    try {
      botDetection = await performBotDetection();
    } catch (botError) {
      console.warn('Bot detection skipped:', botError.message);
    }

    // Only block obvious automated access (score > 80)
    if (botDetection.isBot && botDetection.botScore > 80) {
      throw new Error('Automated access detected. Please try again from a regular browser.');
    }

    // Verify CAPTCHA if provided (optional — don't require it)
    if (captchaResponse) {
      try {
        const captchaResult = await verifyCaptcha(captchaResponse);
        if (!captchaResult.success) {
          console.warn('CAPTCHA verification failed, proceeding anyway');
        }
      } catch (captchaError) {
        console.warn('CAPTCHA check skipped:', captchaError.message);
      }
    }

    // Attempt login
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;

    // Check if user is blocked (only for previously flagged bots)
    try {
      await blockUserIfBot(user.uid);
    } catch (blockError) {
      console.warn('Bot block check skipped:', blockError.message);
    }

    // Get user profile
    const userDoc = await getDoc(doc(db, 'users', user.uid));
    const userData = userDoc.exists() ? userDoc.data() : null;

    // Check if user is approved (allow admins through regardless)
    const isAdminEmail = email.toLowerCase() === 'admin@the-apn.com' ||
                         email.toLowerCase() === 'cburnham@the-apn.com';

    if (!userData) {
      // User exists in Auth but not Firestore — create their doc
      if (isAdminEmail) {
        const newUserData = {
          uid: user.uid,
          email: email.toLowerCase(),
          displayName: user.displayName || 'Admin',
          role: 'admin',
          status: 'approved',
          memberSince: serverTimestamp(),
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          loginCount: 1,
          lastLogin: serverTimestamp()
        };
        await setDoc(doc(db, 'users', user.uid), newUserData);
        return { user, userData: newUserData, requiresAdditionalVerification: false };
      }
      await signOut(auth);
      throw new Error('Account not found. Please register first.');
    }

    if (userData.status !== 'approved' && !isAdminEmail) {
      if (userData.status === 'pending') {
        await signOut(auth);
        throw new Error('Your account is pending approval. You will receive an email once approved.');
      } else if (userData.status === 'suspended') {
        await signOut(auth);
        throw new Error('Your account has been suspended. Please contact support.');
      }
      await signOut(auth);
      throw new Error('Account access denied. Please contact support.');
    }

    // Check for bot flags in user data (only if explicitly flagged)
    if (userData.botDetection?.flagged && !isAdminEmail) {
      await signOut(auth);
      throw new Error('Account has been flagged for review. Please contact support.');
    }

    // Track successful login (non-blocking)
    try {
      trackLoginAttempt(user.uid, true, {
        botScore: botDetection.botScore,
        captchaUsed: !!captchaResponse
      });
    } catch (trackError) {
      console.warn('Login tracking skipped:', trackError.message);
    }

    // Update last login (non-blocking)
    try {
      await updateDoc(doc(db, 'users', user.uid), {
        lastLogin: serverTimestamp(),
        loginCount: (userData.loginCount || 0) + 1
      });
    } catch (updateError) {
      console.warn('Login count update skipped:', updateError.message);
    }

    return {
      user,
      userData,
      requiresAdditionalVerification: false
    };

  } catch (error) {
    // Track failed login attempt (non-blocking)
    try {
      if (error.code !== 'auth/user-not-found') {
        const tempUserId = `temp_${email.replace('@', '_').replace('.', '_')}`;
        trackLoginAttempt(tempUserId, false, {
          error: error.message,
          email
        });
      }
    } catch (trackError) {
      // Silently ignore tracking errors
    }
    throw error;
  }
};

// Enhanced registration with bot detection
export const enhancedRegister = async (userData, captchaResponse, options = {}) => {
  try {
    // Perform bot detection
    const botDetection = await performBotDetection();

    // For now, only block obvious bots (score > 80) to allow legitimate users
    if (botDetection.isBot && botDetection.botScore > 80) {
      throw new Error('Automated registration detected. Please verify you are human.');
    }

    // Verify CAPTCHA if provided
    if (captchaResponse) {
      const captchaResult = await verifyCaptcha(captchaResponse);
      if (!captchaResult.success) {
        throw new Error('CAPTCHA verification failed. Please try again.');
      }
    }

    // Validate email exists
    if (!userData.email || typeof userData.email !== 'string') {
      throw new Error('Valid email is required for registration.');
    }

    // Normalize email
    const normalizedEmail = userData.email.trim().toLowerCase();
    console.log('🔍 Registration attempt for email:', normalizedEmail);

    // ============================================================
    // SECURITY GATES — incident APN-INC-2026-04-09-001
    // Checks A+B via shared helper; Check C inline (needs DISCOUNT_CODES).
    // ============================================================
    validateSignupIdentity(normalizedEmail, userData.firstName, userData.lastName, userData.displayName, 'register');

    // Check C — unvalidated discount code block (server-side whitelist enforcement)
    // NOTE: appliedDiscount is React form state local to MembershipRegistration.js and not in scope here.
    // Server-side defense-in-depth: any discountCode that reaches this layer MUST exist in the
    // DISCOUNT_CODES whitelist. This blocks both the React-form bypass and direct API callers.
    if (userData.discountCode && String(userData.discountCode).trim()) {
      const submittedCode = String(userData.discountCode).trim().toUpperCase();
      if (!DISCOUNT_CODES[submittedCode]) {
        console.warn('[SECURITY] signup rejected:', { check: 'C', email: normalizedEmail, code: submittedCode, timestamp: new Date().toISOString() });
        throw new Error('Invalid discount code. Please click \'Apply\' to validate your code, or leave the field blank.');
      }
    }
    // ============================================================

    // Check for existing users with same email
    const existingUserQuery = query(
      collection(db, 'users'),
      where('email', '==', normalizedEmail)
    );
    const existingUsers = await getDocs(existingUserQuery);
    console.log('🔍 Firestore check - existing users found:', existingUsers.size);

    if (!existingUsers.empty) {
      console.log('❌ User exists in Firestore');
      throw new Error('An account with this email already exists.');
    }

    console.log('✅ Email not found in Firestore, proceeding to Firebase Auth...');

    // Create user account - handle case where Firebase Auth user exists but Firestore doc doesn't
    let userCredential;
    try {
      console.log('🔍 Attempting to create Firebase Auth user...');
      userCredential = await createUserWithEmailAndPassword(
        auth,
        normalizedEmail,
        userData.password
      );
      console.log('✅ Firebase Auth user created successfully');
    } catch (error) {
      console.log('❌ Firebase Auth error:', error.code, error.message);
      if (error.code === 'auth/email-already-in-use') {
        // Check if this is an orphaned auth user (exists in Auth but not in Firestore)
        if (existingUsers.empty) {
          console.log('🔍 Orphaned auth user detected - exists in Auth but not Firestore');
          console.log('🔧 Attempting to sign in to existing auth user and create Firestore profile...');

          try {
            // Sign in to the existing auth user
            const existingUserCredential = await signInWithEmailAndPassword(
              auth,
              normalizedEmail,
              userData.password
            );
            console.log('✅ Successfully signed in to existing auth user');
            userCredential = existingUserCredential;
          } catch (signInError) {
            console.log('❌ Failed to sign in to existing auth user:', signInError.message);
            throw new Error(`This email exists in Firebase Auth but the password doesn't match. Please try logging in or use password reset. If you believe this is an error, please contact support.`);
          }
        } else {
          console.log('🔍 User exists in both Auth and Firestore');
          throw new Error('An account with this email already exists. Please try logging in instead.');
        }
      } else {
        throw error;
      }
    }
    const user = userCredential.user;

    // Update user profile
    await updateProfile(user, {
      displayName: `${userData.firstName} ${userData.lastName}`
    });

    // Sanitize bot detection data to remove undefined values
    const sanitizeBotDetectionData = (data) => {
      if (!data || typeof data !== 'object') return {};

      const sanitized = {};
      for (const [key, value] of Object.entries(data)) {
        if (value !== undefined && value !== null) {
          if (typeof value === 'object' && !Array.isArray(value)) {
            sanitized[key] = sanitizeBotDetectionData(value);
          } else {
            sanitized[key] = value;
          }
        }
      }
      return sanitized;
    };

    const shouldAutoApprove = false; // SECURITY: emergency kill switch — incident APN-INC-2026-04-09-001. All signups require manual admin approval until server-side discount code validation is implemented.

    // Create user document with enhanced security
    const userDocData = {
      ...userData,
      uid: user.uid,
      email: normalizedEmail,
      role: shouldAutoApprove ? USER_ROLES.MEMBER : USER_ROLES.PENDING,
      status: shouldAutoApprove ? 'approved' : 'pending',
      memberSince: shouldAutoApprove ? serverTimestamp() : null,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      loginCount: 0,
      botDetection: {
        registrationBotScore: botDetection.botScore || 0,
        registrationChecks: sanitizeBotDetectionData(botDetection.checks || {}),
        flagged: false
      },
      securityFlags: {
        requiresAdditionalVerification: (botDetection.botScore || 0) > 30,
        lastSecurityCheck: serverTimestamp()
      }
    };

    // Remove password from stored data
    delete userDocData.password;

    await setDoc(doc(db, 'users', user.uid), userDocData);

    // Notify admins about new user registration (only if needs approval)
    if (!shouldAutoApprove) {
      try {
        const { notifyAdminsNewUser } = await import('./notificationService');
        await notifyAdminsNewUser(
          userData.displayName || `${userData.firstName} ${userData.lastName}`,
          normalizedEmail
        );
      } catch (error) {
        console.error('Error notifying admins about new user:', error);
        // Don't fail registration if notification fails
      }
    }

    // Sign out users who still need approval — UNLESS they're being sent to the
    // payment page next (paid membership), where they must stay authenticated to
    // create the Stripe checkout session. They remain a pending user (gated by
    // role) until an admin approves them.
    if (!shouldAutoApprove && !options.keepSession) {
      await signOut(auth);
    }

    return {
      success: true,
      message: shouldAutoApprove
        ? 'Registration successful! Welcome to APN!'
        : 'Registration successful! Your account is pending approval.',
      userId: user.uid,
      autoApproved: shouldAutoApprove
    };

  } catch (error) {
    throw error;
  }
};

// Check user permissions
export const hasPermission = (userRole, permission) => {
  if (!userRole || !permission) return false;
  
  const rolePermissions = ROLE_PERMISSIONS[userRole] || [];
  
  // Super admin has all permissions
  if (rolePermissions.includes('all_permissions')) return true;
  
  return rolePermissions.includes(permission);
};

// Check multiple permissions
export const hasAnyPermission = (userRole, permissions) => {
  return permissions.some(permission => hasPermission(userRole, permission));
};

// Check if user has required role
export const hasRole = (userRole, requiredRole) => {
  const roleHierarchy = {
    [USER_ROLES.GUEST]: 0,
    [USER_ROLES.PENDING]: 1,
    [USER_ROLES.MEMBER]: 2,
    [USER_ROLES.VIP_MEMBER]: 3,
    [USER_ROLES.CHAPTER_PRESIDENT]: 4,
    [USER_ROLES.ADMIN]: 5,
    [USER_ROLES.SUPER_ADMIN]: 6
  };

  const userLevel = roleHierarchy[userRole] || 0;
  const requiredLevel = roleHierarchy[requiredRole] || 0;

  return userLevel >= requiredLevel;
};

// Enhanced logout with cleanup
export const enhancedLogout = async () => {
  try {
    await signOut(auth);
    
    // Clear any cached data
    sessionStorage.clear();
    
    // Clear any bot detection tracking for this session
    // (This would be handled by the bot detection service)
    
    return { success: true };
  } catch (error) {
    throw error;
  }
};

// Verify user session and check for security issues
export const verifyUserSession = async (user) => {
  try {
    if (!user) return { valid: false, reason: 'No user session' };

    // Get current user data
    const userDoc = await getDoc(doc(db, 'users', user.uid));
    const userData = userDoc.exists() ? userDoc.data() : null;

    // If no Firestore doc exists, session is still valid (user exists in Auth)
    // The doc may not have been created yet or may be in progress
    if (!userData) {
      return { valid: true, userData: null, requiresAdditionalVerification: false };
    }

    // Only invalidate for explicitly suspended accounts
    if (userData.status === 'suspended') {
      return { valid: false, reason: 'Account suspended. Please contact support.' };
    }

    // Check for bot flags
    if (userData.botDetection?.flagged) {
      return { valid: false, reason: 'Account flagged for suspicious activity' };
    }

    return {
      valid: true,
      userData,
      requiresAdditionalVerification: false
    };

  } catch (error) {
    // On error, don't invalidate the session — just log and continue
    console.warn('Session verification error (non-blocking):', error.message);
    return { valid: true, userData: null, requiresAdditionalVerification: false };
  }
};

// Update user security status
export const updateUserSecurityStatus = async (userId, securityData) => {
  try {
    await updateDoc(doc(db, 'users', userId), {
      'securityFlags.lastSecurityCheck': serverTimestamp(),
      'securityFlags.requiresAdditionalVerification': securityData.requiresVerification || false,
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error updating user security status:', error);
  }
};

export default {
  enhancedLogin,
  enhancedRegister,
  enhancedLogout,
  hasPermission,
  hasAnyPermission,
  hasRole,
  verifyUserSession,
  updateUserSecurityStatus,
  USER_ROLES,
  ROLE_PERMISSIONS
};
